import React, {
    useRef,
    PropsWithChildren,
    useCallback,
    useMemo,
    useEffect
} from "react";
import useKeyPress from "hooks/useKeyPress";
import styles from "./index.module.scss";
import Close from "assets/icons/close.svg?react";
import { useDetectStripeModal } from "helpers/useDetectStripeModal";
import { useSwitcherSdk } from "hooks/useSwitcherSdk";

export interface ModalButtonProps {
    text: string;
    onClick: (...args: any) => any;
    disabled?: boolean;
    shouldCloseOnClick?: boolean;
}

export interface ModalBaseProps extends PropsWithChildren {
    isOpen: boolean;
    setIsOpen?: (open: boolean) => void;
    header?: string;
    /** Props for the primary button --- used for more complicated modals */
    primaryButton?: ModalButtonProps;
    /** Props for the secondary button --- used for more complicated modals */
    secondaryButton?: ModalButtonProps;
    /** Props for the tertiary button --- used for more complicated modals */
    tertiaryButton?: ModalButtonProps;
    successButton?: string;
    dismissButton?: string;
    onSuccess?: (...args: any) => any;
    shouldNotCloseOnSuccess?: boolean;
    successDisabled?: boolean;
    onDismiss?: (...args: any) => any;
    shouldHideButtons?: boolean;
    preventDismiss?: boolean;
    /** Showing loading state of modal */
    loading?: boolean;
    detect3DSecureModal?: boolean;
    variant?: "default" | "warning" | "video-playback" | "fullscreen";
}

/** Base modal implementation used for building new modal instances.
 * For simple modals you can utilize successButton, dismissButton, onSuccess, shouldNotCloseOnSuccess, successDisabled, onDismiss props
 * to handle the two main button actions
 *
 * For more complex, multi-step modals you may not want the "secondary" button to have a "dismiss" functionality. In this case you can customize
 * button behavior using the primaryButton and secondaryButton props objects instead of the successButton and dismissButton props. (onDismiss is still needed in this case)
 */
export const ModalBase: React.FC<ModalBaseProps> = ({
    isOpen,
    setIsOpen,
    header,
    primaryButton,
    secondaryButton,
    tertiaryButton,
    onDismiss,
    onSuccess,
    successButton,
    dismissButton,
    shouldNotCloseOnSuccess,
    successDisabled,
    shouldHideButtons,
    children,
    preventDismiss,
    loading,
    detect3DSecureModal = false,
    variant = "default"
}) => {
    const modalRef = useRef<HTMLDialogElement>(null);
    const { isInBrowser } = useSwitcherSdk();

    const close = useCallback(() => {
        if (!preventDismiss) {
            setIsOpen && setIsOpen(false);
            onDismiss && onDismiss();
        }
    }, [onDismiss, preventDismiss, setIsOpen]);

    // triggers show/hide modal and adds/removes event listener for closing modal on click outside
    useEffect(() => {
        const modal = modalRef?.current;
        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            if (event.target === modalRef.current) {
                close();
            }
        };

        modal?.addEventListener("mousedown", handlePointerDown);
        modal?.addEventListener("touchstart", handlePointerDown);

        if (isOpen) {
            modal?.showModal();
        } else {
            modal?.close();
        }

        return () => {
            modal?.removeEventListener("mousedown", handlePointerDown);
            modal?.removeEventListener("touchstart", handlePointerDown);
        };
    }, [isOpen, close]);

    const threeDSecureModalOpen = useDetectStripeModal({
        polling: detect3DSecureModal
    });

    /** Returns either the default success button attributes OR attributes from custom props */
    const primaryButtonObject: ModalButtonProps = useMemo(() => {
        if (successButton) {
            return {
                text: successButton,
                onClick: () => {
                    onSuccess();
                },
                disabled: successDisabled,
                shouldCloseOnClick: !shouldNotCloseOnSuccess
            };
        } else {
            return primaryButton;
        }
    }, [
        onSuccess,
        primaryButton,
        shouldNotCloseOnSuccess,
        successButton,
        successDisabled
    ]);

    /** Returns either the default dismiss button attributes OR attributes from custom props */
    const secondaryButtonObject: ModalButtonProps = useMemo(() => {
        if (dismissButton) {
            return {
                text: dismissButton,
                onClick: null,
                shouldCloseOnClick: true,
                disabled: false
            };
        } else {
            return secondaryButton;
        }
    }, [dismissButton, secondaryButton]);

    const handleButtonClick = useCallback(
        (btnProps: ModalButtonProps) => {
            btnProps?.onClick && btnProps.onClick();
            if (btnProps?.shouldCloseOnClick) {
                close();
            }
        },
        [close]
    );

    useEffect(() => {
        if (
            threeDSecureModalOpen &&
            modalRef.current &&
            modalRef.current.open
        ) {
            modalRef.current.close();
        } else if (
            !threeDSecureModalOpen &&
            modalRef.current &&
            !modalRef.current.open
        ) {
            modalRef.current.showModal();
        }
    }, [threeDSecureModalOpen]);

    useKeyPress("Escape", close);

    const isInWebView = useMemo(() => {
        // check if is in app webview
        return !isInBrowser && variant === "fullscreen";
    }, [isInBrowser, variant]);

    return (
        <>
            {isOpen ? (
                <dialog
                    className={`switcher-modal ${styles["modal"]} ${
                        loading ? styles["loading"] : ""
                    }`}
                    ref={modalRef}
                >
                    <div
                        className={`modal-wrapper ${styles["modal-wrapper"]} ${
                            isInWebView ? styles["fullscreen"] : ""
                        }`}
                    >
                        {header && (
                            <>
                                <div className={`header ${styles["header"]}`}>
                                    <div
                                        className={`header-content ${styles["header-content"]}`}
                                    >
                                        <h5
                                            className={
                                                variant === "warning"
                                                    ? styles["warning"]
                                                    : ""
                                            }
                                        >
                                            {header}
                                        </h5>
                                        {!preventDismiss && (
                                            <button
                                                type="button"
                                                onClick={close}
                                                aria-label="Close modal"
                                            >
                                                <Close />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <hr
                                    className={`header-break ${styles["break"]}`}
                                />
                            </>
                        )}
                        <div
                            className={`modal-children ${
                                styles["modal-children"]
                            } ${
                                variant === "video-playback"
                                    ? styles["video-playback"]
                                    : ""
                            }`}
                        >
                            {children}
                        </div>
                        {!shouldHideButtons &&
                            (primaryButtonObject ||
                                secondaryButtonObject ||
                                tertiaryButton) && (
                                <>
                                    <hr className={styles["break"]} />
                                    <div
                                        className={`${styles["footer"]}${
                                            !primaryButtonObject?.text &&
                                            !tertiaryButton?.text
                                                ? ` ${styles["footer-right"]}`
                                                : ""
                                        }`}
                                    >
                                        {secondaryButtonObject?.text && (
                                            <button
                                                className="btn"
                                                onClick={() =>
                                                    handleButtonClick(
                                                        secondaryButtonObject
                                                    )
                                                }
                                            >
                                                {secondaryButtonObject?.text}
                                            </button>
                                        )}
                                        {tertiaryButton?.text && (
                                            <button
                                                className="btn"
                                                onClick={() =>
                                                    handleButtonClick(
                                                        tertiaryButton
                                                    )
                                                }
                                            >
                                                {tertiaryButton?.text}
                                            </button>
                                        )}
                                        {primaryButtonObject?.text && (
                                            <button
                                                className={`btn btn-primary ${styles["success-button"]}`}
                                                onClick={() =>
                                                    handleButtonClick(
                                                        primaryButtonObject
                                                    )
                                                }
                                                disabled={
                                                    primaryButtonObject?.disabled
                                                }
                                            >
                                                {primaryButtonObject?.text}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                    </div>
                </dialog>
            ) : (
                <></>
            )}
        </>
    );
};
